import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { startupServices } from '../data/services';
import ServiceCard from '../components/common/ServiceCard';

const StartupServices = () => {
  const benefits = [
    'Expert guidance through entire registration process',
    'All documentation handled by professionals',
    'Quick turnaround time',
    'Post-registration support included',
    'Transparent pricing with no hidden charges',
    'PAN India service coverage',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="startup-services-page">
      <Helmet>
        <title>Company Registration Services - Private Limited, LLP, OPC | GK Legal Solutions</title>
        <meta name="description" content="Register your company with GK Legal Solutions. Private Limited, LLP, OPC, Section 8, Trust/NGO Registration services. Quick processing, expert guidance." />
        <link rel="canonical" href="https://gklegalsolutions.com/startup-services" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Startup Services</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 mb-6">
                Start Your Business Journey Today
              </h1>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                Choose the right business structure for your venture. From sole proprietorship to private limited company, we help you register and launch your business with ease.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary flex items-center gap-2" data-testid="startup-get-started-btn">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+919820669943">
                  <Button variant="outline" className="btn-secondary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call for Consultation
                  </Button>
                </a>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800"
                alt="Startup team working"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Choose Your Business Structure
            </h2>
            <p className="font-body text-lg text-slate-600">
              Select the business type that best suits your requirements and growth plans.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {startupServices.map((service) => (
              <ServiceCard key={service.id} service={service} linkTo="/contact" />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-8">
                We Make Company Registration Simple
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="font-body text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-primary text-white p-8 rounded-xl">
                <p className="font-heading text-4xl font-bold mb-2">500+</p>
                <p className="font-body text-white/80">Companies Registered</p>
              </div>
              <div className="bg-slate-900 text-white p-8 rounded-xl">
                <p className="font-heading text-4xl font-bold mb-2">3-5</p>
                <p className="font-body text-white/80">Days Processing</p>
              </div>
              <div className="bg-slate-900 text-white p-8 rounded-xl">
                <p className="font-heading text-4xl font-bold mb-2">100%</p>
                <p className="font-body text-white/80">Success Rate</p>
              </div>
              <div className="bg-primary text-white p-8 rounded-xl">
                <p className="font-heading text-4xl font-bold mb-2">24/7</p>
                <p className="font-body text-white/80">Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Register Your Company?
          </h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get expert guidance and start your business journey today. First consultation is free!
          </p>
          <Link to="/contact">
            <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold">
              Start Registration Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StartupServices;
